import React, { memo } from 'react';
import { Card, CardContent, Box, Typography, IconButton, FormControl, InputLabel, Select, MenuItem, Checkbox, Button, Collapse } from '@mui/material';
import { ZoomOutMap, ExpandMore, PushPin, PushPinOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { reportStyles, reportMenuPropsWithScroll } from '../../../shared/globalSX';
import { ICONS } from '../../../shared/icons';
import { PieChartView, BarChartView } from '../ChartComponents';

const StatusChart = memo(({ data, allData, chartView, setChartView, onZoomClick, selectedStatuses, setSelectedStatuses, availableStatuses, defaultExpanded = true, isPinned = false, onPin }) => {
  const { t } = useTranslation();
  // If isPinned is true, we force the chart to be expanded
  const [isExpanded, setIsExpanded] = React.useState(isPinned || defaultExpanded);

  // Sync isExpanded with isPinned when it changes
  React.useEffect(() => {
    if (isPinned) {
      setIsExpanded(true);
    }
  }, [isPinned]);

  return (
    <Card sx={{ height: 'auto', transition: 'height 0.3s' }} data-chart-type="status">
      <CardContent sx={{ pb: isExpanded ? 0 : '16px !important', '&:last-child': { pb: isExpanded ? 0 : '16px !important' } }}>
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          pb={1.5}
          mb={isExpanded ? 2 : 0}
          sx={{ borderBottom: '1px solid #082B72' }}
        >
          <Box display="flex" alignItems="center" gap={1} sx={{ cursor: 'pointer' }} onClick={() => setIsExpanded(!isExpanded)}>
            <IconButton size="small" sx={{ color: 'text.secondary', p: 0 }}>
              {isExpanded ? <ExpandMore /> : <ExpandMore sx={{ transform: 'rotate(-90deg)' }} />}
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 500, color: '#082B72' }}>{t('reports.charts.statusVsTicketCount')}</Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <Box display="flex" gap={1} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>{t('reports.statusSelection.selectStatuses')}</InputLabel>
                <Select
                  multiple
                  value={selectedStatuses}
                  onChange={(e) => setSelectedStatuses(e.target.value)}
                  label={t('reports.statusSelection.selectStatuses')}
                  MenuProps={reportMenuPropsWithScroll}
                  sx={reportStyles.reportSelect}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', minWidth: 0 }}>
                      {selected.slice(0, 3).map((status) => (
                        <Typography
                          key={status}
                          component="span"
                          variant="body2"
                          sx={{
                            color: allData.find(s => s.name === status)?.color || 'text.primary',
                            fontSize: '13px',
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {status}
                        </Typography>
                      ))}
                      {selected.length > 3 && (
                        <Typography component="span" variant="body2" sx={{ color: 'text.secondary', fontSize: '13px', fontWeight: 500 }}>
                          +{selected.length - 3}
                        </Typography>
                      )}
                    </Box>
                  )}
                >
                  {availableStatuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      <Checkbox checked={selectedStatuses.indexOf(status) > -1} />
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: allData.find(s => s.name === status)?.color
                          }}
                        />
                        {status}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Box component="img" src={ICONS.pieChart} sx={{ width: 18, height: 18 }} />}
                onClick={() => setChartView('pie')}
                sx={reportStyles.buttonOutlined}
              >
                {t("reports.charts.pie")}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Box component="img" src={ICONS.barGraph} sx={{ width: 18, height: 18 }} />}
                onClick={() => setChartView('bar')}
                sx={reportStyles.buttonOutlined}
              >
                {t("reports.charts.bar")}
              </Button>
            </Box>
          </Box>
        </Box>

        <Collapse in={isExpanded}>
          <Box sx={{ mt: 2, position: 'relative', height: data.length > 0 ? 390 : 'auto' }}>
            {data.length === 0 ? (
              <Box sx={{ height: 330, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">{t("reports.noDataAvailable")}</Typography>
              </Box>
            ) : (
              <>
                {chartView === 'pie' && <PieChartView data={data} height={350} />}
                {chartView === 'bar' && <BarChartView data={data} height={350} variant="status" />}

                {/* Fixed Bottom Right Actions with Premium Shadow and Click Effect */}
                <Box sx={{ 
                  position: 'absolute', 
                  bottom: 12, 
                  right: 12, 
                  display: 'flex', 
                  gap: 1.5,
                  zIndex: 2
                }}>
                  <IconButton 
                    size="small" 
                    onClick={onPin} 
                    sx={{ 
                      color: isPinned ? '#082B72' : 'text.secondary',
                      bgcolor: 'white',
                      borderRadius: 1,
                      boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                      '&:hover': { bgcolor: '#f8fafc', boxShadow: '0 4px 12px rgba(0,0,0,0.18)' },
                      '&:active': { transform: 'scale(0.92)' },
                      transition: 'all 0.15s ease',
                      p: 0.75 
                    }}
                  >
                    {isPinned ? <PushPin fontSize="small" /> : <PushPinOutlined fontSize="small" />}
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={onZoomClick} 
                    sx={{ 
                      color: 'text.secondary',
                      bgcolor: 'white',
                      borderRadius: 1,
                      boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                      '&:hover': { bgcolor: '#f8fafc', boxShadow: '0 4px 12px rgba(0,0,0,0.18)' },
                      '&:active': { transform: 'scale(0.92)' },
                      transition: 'all 0.15s ease',
                      p: 0.75 
                    }}
                  >
                    <ZoomOutMap fontSize="small" />
                  </IconButton>
                </Box>
              </>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}, (prevProps, nextProps) =>
  prevProps.data === nextProps.data &&
  prevProps.chartView === nextProps.chartView &&
  prevProps.selectedStatuses === nextProps.selectedStatuses &&
  prevProps.defaultExpanded === nextProps.defaultExpanded &&
  prevProps.isPinned === nextProps.isPinned
);

export default StatusChart;
